import { neutral2, neutral4, neutral8 } from '../../colors';
import { fontSmoothing, getFontFamily } from '../../typography';
import idsTokensDefault from '../../utils/tokens/idsTokens';

export const classes = (idsTokens = idsTokensDefault) => ({
  root: {
    whiteSpace: 'nowrap',
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    fontWeight: idsTokens.typography.font.weight.medium,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.lg,
    '& svg': {
      marginRight: 0,
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&:focus-visible': {
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.button.default.color.outline}`,
      outlineOffset: 2,
    },
  },
  contained: {
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: neutral4,
    },
    '&:active': {
      boxShadow: 'unset',
    },
    '&.Mui-disabled': {
      backgroundColor: neutral2,
      color: neutral8,
    },
  },

  // Primary Button
  containedPrimary: {
    backgroundColor: idsTokens.button.default.color.primary.background.enabled,
    border: `1px solid ${idsTokens.button.default.color.primary.border.enabled}`,
    color: idsTokens.button.default.color.primary.content.enabled,
    boxShadow: `${idsTokens.button.default.shadow.x} ${idsTokens.button.default.shadow.y} ${idsTokens.button.default.shadow.blur} ${idsTokens.button.default.shadow.spread} ${idsTokens.button.default.color.primary.shadow.enabled}`,
    paddingTop: `calc(${idsTokens.button.default.spacing.standard.primary.padding.top} - 1px)`,
    paddingRight: idsTokens.button.default.spacing.standard.primary.padding.right,
    paddingBottom: idsTokens.button.default.spacing.standard.primary.padding.bottom,
    paddingLeft: idsTokens.button.default.spacing.standard.primary.padding.left,
    minHeight: idsTokens.button.default.spacing.standard.primary.min.height,
    minWidth: idsTokens.button.default.spacing.standard.primary.min.width,
    gap: idsTokens.button.default.spacing.standard.primary.padding.gap,
    borderRadius: idsTokens.button.default.radius.standard.primary,
    '&:hover': {
      backgroundColor: idsTokens.button.default.color.primary.background.enabled,
      boxShadow: `${idsTokens.button.default.shadow.x} ${idsTokens.button.default.shadow.y} ${idsTokens.button.default.shadow.blur} ${idsTokens.button.default.shadow.spread} ${idsTokens.button.default.color.primary.shadow.enabled}`,
      color: idsTokens.button.default.color.primary.content.enabled,
      border: `1px solid ${idsTokens.button.default.color.primary.border.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.default.color.primary.background.hover,
        boxShadow: `${idsTokens.button.default.shadow.x} ${idsTokens.button.default.shadow.y} ${idsTokens.button.default.shadow.blur} ${idsTokens.button.default.shadow.spread} ${idsTokens.button.default.color.primary.shadow.hover}`,
        color: idsTokens.button.default.color.primary.content.hover,
        border: `1px solid ${idsTokens.button.default.color.primary.border.hover}`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.default.color.primary.background.disabled,
      color: idsTokens.button.default.color.primary.content.disabled,
      borderColor: idsTokens.button.default.color.primary.border.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.default.color.primary.background.focus,
      color: idsTokens.button.default.color.primary.content.focus,
      boxShadow: 'none',
      zIndex: 1,
    },
    '&:active': {
      color: idsTokens.button.default.color.primary.content.pressed,
      border: `1px solid ${idsTokens.button.default.color.primary.border.pressed}`,
      backgroundColor: idsTokens.button.default.color.primary.background.pressed,
      boxShadow: `${idsTokens.button.default.shadowPressed.x} ${idsTokens.button.default.shadowPressed.y} ${idsTokens.button.default.shadowPressed.blur} ${idsTokens.button.default.shadowPressed.spread} ${idsTokens.button.default.color.primary.shadow.pressed}`,
    },
  },

  //Secondary Button
  outlinedSecondary: {
    color: idsTokens.button.default.color.secondary.content.enabled,
    backgroundColor: idsTokens.button.default.color.secondary.background.enabled,
    border: `1px solid ${idsTokens.button.default.color.secondary.border.enabled}`,
    minHeight: idsTokens.button.default.spacing.standard.secondary.min.height,
    minWidth: idsTokens.button.default.spacing.standard.secondary.min.width,
    paddingTop: `calc(${idsTokens.button.default.spacing.standard.secondary.padding.top} - 1px)`,
    paddingRight: idsTokens.button.default.spacing.standard.secondary.padding.right,
    paddingBottom: idsTokens.button.default.spacing.standard.secondary.padding.bottom,
    paddingLeft: idsTokens.button.default.spacing.standard.secondary.padding.left,
    gap: idsTokens.button.default.spacing.standard.secondary.padding.gap,
    borderRadius: idsTokens.button.default.radius.standard.secondary,
    '&:hover': {
      backgroundColor: idsTokens.button.default.color.secondary.background.enabled,
      border: `1px solid ${idsTokens.button.default.color.secondary.border.enabled}`,
      color: idsTokens.button.default.color.secondary.content.enabled,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.default.color.secondary.background.hover,
        border: `1px solid ${idsTokens.button.default.color.secondary.border.hover}`,
        color: idsTokens.button.default.color.secondary.content.hover,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.default.color.secondary.background.disabled,
      color: idsTokens.button.default.color.secondary.content.disabled,
      borderColor: idsTokens.button.default.color.secondary.border.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      border: `1px solid ${idsTokens.button.default.color.secondary.border.focus}`,
      backgroundColor: idsTokens.button.default.color.secondary.background.focus,
      color: idsTokens.button.default.color.secondary.content.focus,
      zIndex: 1,
    },
    '&:active': {
      color: idsTokens.button.default.color.secondary.content.pressed,
      border: `1px solid ${idsTokens.button.default.color.secondary.border.pressed}`,
      backgroundColor: idsTokens.button.default.color.secondary.background.pressed,
    },
  },

  //Tertiary Button
  containedSecondary: {
    backgroundColor: idsTokens.button.default.color.tertiary.background.enabled,
    border: `1px solid ${idsTokens.button.default.color.tertiary.border.enabled}`,
    color: idsTokens.button.default.color.tertiary.content.enabled,
    boxShadow: 'none',
    paddingTop: `calc(${idsTokens.button.default.spacing.standard.tertiary.padding.top} - 1px)`,
    paddingRight: idsTokens.button.default.spacing.standard.tertiary.padding.right,
    paddingBottom: idsTokens.button.default.spacing.standard.tertiary.padding.bottom,
    paddingLeft: idsTokens.button.default.spacing.standard.tertiary.padding.left,
    minHeight: idsTokens.button.default.spacing.standard.tertiary.min.height,
    minWidth: idsTokens.button.default.spacing.standard.tertiary.min.width,
    gap: idsTokens.button.default.spacing.standard.tertiary.padding.gap,
    borderRadius: idsTokens.button.default.radius.standard.tertiary,
    '&:hover': {
      backgroundColor: idsTokens.button.default.color.tertiary.background.enabled,
      boxShadow: 'none',
      color: idsTokens.button.default.color.tertiary.content.enabled,
      border: `1px solid ${idsTokens.button.default.color.tertiary.border.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.default.color.tertiary.background.hover,
        boxShadow: 'none',
        color: idsTokens.button.default.color.tertiary.content.hover,
        border: `1px solid ${idsTokens.button.default.color.tertiary.border.hover}`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.default.color.tertiary.background.disabled,
      color: idsTokens.button.default.color.tertiary.content.disabled,
      borderColor: idsTokens.button.default.color.tertiary.border.disabled,
      opacity: 1,
      boxShadow: 'none',
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.default.color.tertiary.background.focus,
      color: idsTokens.button.default.color.tertiary.content.focus,
      boxShadow: 'none',
      zIndex: 1,
    },
    '&:active': {
      color: idsTokens.button.default.color.tertiary.content.pressed,
      border: `1px solid ${idsTokens.button.default.color.tertiary.border.pressed}`,
      backgroundColor: idsTokens.button.default.color.tertiary.background.pressed,
    },
  },

  //Discreet Button
  text: {
    paddingTop: `calc(${idsTokens.button.default.spacing.standard.discreet.padding.top} - 1px)`,
    paddingRight: idsTokens.button.default.spacing.standard.discreet.padding.right,
    paddingBottom: idsTokens.button.default.spacing.standard.discreet.padding.bottom,
    paddingLeft: idsTokens.button.default.spacing.standard.discreet.padding.left,
    gap: idsTokens.button.default.spacing.standard.discreet.padding.gap,
    color: idsTokens.button.default.color.discreet.content.enabled,
    minWidth: idsTokens.button.default.spacing.standard.discreet.min.width,
    minHeight: idsTokens.button.default.spacing.standard.discreet.min.height,
    borderRadius: idsTokens.button.default.radius.standard.discreet,
    backgroundColor: idsTokens.button.default.color.discreet.background.enabled,
    lineHeight: '150%',
    border: 'unset',
    '&:hover': {
      backgroundColor: idsTokens.button.default.color.discreet.background.enabled,
      color: idsTokens.button.default.color.discreet.content.enabled,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.default.color.discreet.background.hover,
        color: idsTokens.button.default.color.discreet.content.hover,
      },
    },
    '&:active': {
      background: idsTokens.button.default.color.discreet.background.pressed,
      color: idsTokens.button.default.color.discreet.content.pressed,
    },
    '&.Mui-disabled': {
      color: idsTokens.button.default.color.discreet.content.disabled,
      backgroundColor: idsTokens.button.default.color.discreet.background.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.default.color.discreet.background.focus,
      color: idsTokens.button.default.color.discreet.content.focus,
      zIndex: 1,
    },
  },
  sizeSmall: {
    fontSize: idsTokens.typography.font.size.sm,
    lineHeight: idsTokens.typography.lineHeight.sm,
    letterSpacing: idsTokens.typography.spacing.letter.md,
    '&.MuiButton-containedPrimary': {
      minHeight: idsTokens.button.default.spacing.small.primary.min.height,
      minWidth: idsTokens.button.default.spacing.small.primary.min.width,
      paddingTop: idsTokens.button.default.spacing.small.primary.padding.top,
      paddingRight: idsTokens.button.default.spacing.small.primary.padding.right,
      paddingBottom: idsTokens.button.default.spacing.small.primary.padding.bottom,
      paddingLeft: idsTokens.button.default.spacing.small.primary.padding.left,
      gap: idsTokens.button.default.spacing.small.primary.padding.gap,
      borderRadius: idsTokens.button.default.radius.small.primary,
    },
    '&.MuiButton-outlined.MuiButton-colorSecondary': {
      minHeight: idsTokens.button.default.spacing.small.secondary.min.height,
      minWidth: idsTokens.button.default.spacing.small.secondary.min.width,
      paddingTop: idsTokens.button.default.spacing.small.secondary.padding.top,
      paddingRight: idsTokens.button.default.spacing.small.secondary.padding.right,
      paddingBottom: idsTokens.button.default.spacing.small.secondary.padding.bottom,
      paddingLeft: idsTokens.button.default.spacing.small.secondary.padding.left,
      gap: idsTokens.button.default.spacing.small.secondary.padding.gap,
      borderRadius: idsTokens.button.default.radius.small.secondary,
    },
    '&.MuiButton-contained.MuiButton-colorSecondary': {
      minHeight: idsTokens.button.default.spacing.small.tertiary.min.height,
      minWidth: idsTokens.button.default.spacing.small.tertiary.min.width,
      paddingTop: idsTokens.button.default.spacing.small.tertiary.padding.top,
      paddingRight: idsTokens.button.default.spacing.small.tertiary.padding.right,
      paddingBottom: idsTokens.button.default.spacing.small.tertiary.padding.bottom,
      paddingLeft: idsTokens.button.default.spacing.small.tertiary.padding.left,
      gap: idsTokens.button.default.spacing.small.tertiary.padding.gap,
      borderRadius: idsTokens.button.default.radius.small.tertiary,
      boxShadow: 'none',
    },
    '&.MuiButton-text': {
      minHeight: idsTokens.button.default.spacing.small.discreet.min.height,
      minWidth: idsTokens.button.default.spacing.small.discreet.min.width,
      paddingTop: idsTokens.button.default.spacing.small.discreet.padding.top,
      paddingRight: idsTokens.button.default.spacing.small.discreet.padding.right,
      paddingBottom: idsTokens.button.default.spacing.small.discreet.padding.bottom,
      paddingLeft: idsTokens.button.default.spacing.small.discreet.padding.left,
      gap: idsTokens.button.default.spacing.small.discreet.padding.gap,
      borderRadius: idsTokens.button.default.radius.small.discreet,
    },
  },
  label: {
    position: 'relative',
  },
});
